/**
 * Canonical Internet Identity derivation for the Bonsai OS ecosystem.
 * All satellite apps (landing, Cloud, Bazaar, Radio, …) derive principals from Registry.
 *
 * II app branding (name / logo / description) is published once on Registry:
 * `/.well-known/ii-app-metadata` + `/brand/bonsai-os/ii-app-logo.png`.
 * II loads that from the derivation origin — do not duplicate per satellite.
 *
 * @see https://internetcomputer.org/docs/building-apps/authentication/alternative-origins
 * @see https://docs.internetcomputer.org/guides/authentication/app-metadata/
 */
export const BONSAI_OS_CANONICAL_II_DERIVATION_ORIGIN =
  "https://u2jsa-baaaa-aaaau-aghea-cai.icp0.io";

/** Registry assets canister id (mainnet) — used by II alt-origins sync scripts. */
export const BONSAI_OS_REGISTRY_ASSETS_CANISTER_ID = "u2jsa-baaaa-aaaau-aghea-cai";

/** II allows at most 10 entries in alternativeOrigins (icp0.io canonical only in Bonsai OS).
 * Do not list the Registry derivation host itself — only satellite origins that pass derivationOrigin.
 */
export const II_ALTERNATIVE_ORIGINS_MAX = 10;

export function normalizeDerivationOrigin(origin: string): string {
  return origin.trim().replace(/\/$/, "");
}

/** Map IC canister frontends to canonical https://*.icp0.io (one slot per app). */
export function icCanonicalIcp0Origin(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    if (!parsed.hostname.endsWith(".icp0.io") && !parsed.hostname.endsWith(".ic0.app")) {
      return parsed.origin;
    }
    const host = parsed.hostname.endsWith(".ic0.app")
      ? parsed.hostname.replace(/\.ic0\.app$/, ".icp0.io")
      : parsed.hostname;
    return `https://${host}`;
  } catch {
    return null;
  }
}

/** Redirect legacy *.ic0.app bookmarks to *.icp0.io before auth (saves II alt-origin slots). */
export function redirectToCanonicalIcp0Origin(): void {
  if (typeof window === "undefined") return;
  try {
    const u = new URL(window.location.href);
    if (!u.hostname.endsWith(".ic0.app")) return;
    u.hostname = u.hostname.replace(/\.ic0\.app$/, ".icp0.io");
    window.location.replace(u.href);
  } catch {
    /* ignore */
  }
}

/**
 * AuthClient.login / create loginOptions — only pass derivationOrigin when it differs from the page.
 */
export function loginOptionsFromDerivationOrigin(
  configuredOrigin?: string,
  pageOrigin?: string,
): { derivationOrigin?: string } {
  const raw = (configuredOrigin ?? BONSAI_OS_CANONICAL_II_DERIVATION_ORIGIN).trim();
  if (!raw) return {};
  try {
    const configured = normalizeDerivationOrigin(new URL(raw).origin);
    const page =
      pageOrigin ??
      (typeof window !== "undefined" ? window.location.origin : "");
    if (page && normalizeDerivationOrigin(page) === configured) {
      return {};
    }
    return { derivationOrigin: configured };
  } catch {
    return {};
  }
}

/** Add both icp0.io and ic0.app mirrors for an IC assets canister URL. */
export function addIcOriginMirrors(origins: Set<string>, url: string): void {
  try {
    const parsed = new URL(url);
    origins.add(parsed.origin);
    if (parsed.hostname.endsWith(".icp0.io")) {
      origins.add(`https://${parsed.hostname.replace(/\.icp0\.io$/, ".ic0.app")}`);
    } else if (parsed.hostname.endsWith(".ic0.app")) {
      origins.add(`https://${parsed.hostname.replace(/\.ic0\.app$/, ".icp0.io")}`);
    }
  } catch {
    /* ignore */
  }
}
